import { Emote as EmoteType } from 'eolib';
import type { Client } from '@/client';
import { GameState } from '@/game-state';
import { EMOTE_ANIMATION_TICKS } from '@/consts';
import { CharacterDeathAnimation, Emote, NpcDeathAnimation } from '@/render';

type TargetKind = 'npc' | 'player';
type Status = { kind: TargetKind; target: number; entity: object; started: number; until: number; emote: Emote };
type Store = { map: number; nearby: Client['nearby']; statuses: Map<string, Status> };
const stores = new WeakMap<Client, Store>();
const listeners = new WeakMap<Client, () => void>();

function store(client: Client): Store {
  let value = stores.get(client);
  if (!value || value.map !== client.mapId || value.nearby !== client.nearby) {
    value = { map: client.mapId, nearby: client.nearby, statuses: new Map() };
    stores.set(client, value);
  }
  if (client.state !== GameState.InGame || client.worldLoading) value.statuses.clear();
  return value;
}

function entity(client: Client, kind: TargetKind, id: number) {
  return kind === 'npc' ? client.getNpcByIndex(id) : client.getCharacterById(id);
}

function prune(client: Client, value: Store, now: number) {
  for (const [key, status] of value.statuses) {
    const animation = status.kind === 'npc'
      ? client.animationController.npcAnimations.get(status.target)
      : client.animationController.characterAnimations.get(status.target);
    if (status.until <= now || entity(client, status.kind, status.target) !== status.entity ||
      animation instanceof NpcDeathAnimation || animation instanceof CharacterDeathAnimation) value.statuses.delete(key);
  }
}

export function registerWeaponStatusLifecycle(client: Client) {
  const previous = listeners.get(client);
  if (previous) client.unsubscribeStateChanged(previous);
  const clear = () => stores.delete(client);
  listeners.set(client, clear);
  client.subscribeStateChanged(clear);
  clear();
}

/** Optional family54 status union. Never changes gameplay or normal emote state. */
export function receiveWeaponStatus(client: Client, hit: unknown) {
  if (!hit || typeof hit !== 'object') return;
  const p = hit as Record<string, unknown>;
  if (p.kind !== 'weapon_status' || p.map !== client.mapId ||
    client.state !== GameState.InGame || client.worldLoading ||
    (p.target_kind !== 'npc' && p.target_kind !== 'player') || p.effect !== 'sleep' ||
    !Number.isInteger(p.target) || (p.target as number) < 0 ||
    !Number.isInteger(p.duration_ms) || (p.duration_ms as number) < 0 || (p.duration_ms as number) > 300000 ||
    !Number.isInteger(p.x) || !Number.isInteger(p.y) || !client.map ||
    (p.x as number) < 0 || (p.y as number) < 0 || (p.x as number) > client.map.width || (p.y as number) > client.map.height) return;
  const value = store(client), now = performance.now();
  prune(client, value, now);
  const target = p.target as number, key = `${p.target_kind}:${target}:sleep`;
  if (p.duration_ms === 0) { value.statuses.delete(key); return; }
  const recipient = entity(client, p.target_kind, target);
  if (!recipient || (value.statuses.size >= 256 && !value.statuses.has(key))) return;
  value.statuses.set(key, { kind:p.target_kind, target, entity:recipient, started:now,
    until:now+(p.duration_ms as number), emote:new Emote(EmoteType.Moon) });
}

export function getWeaponStatusEmote(client: Client, kind: TargetKind, target: number): Emote | undefined {
  const value = store(client), now = performance.now();
  prune(client, value, now);
  const status = value.statuses.get(`${kind}:${target}:sleep`);
  if (!status) return;
  // Existing atlas has four frames. Keep it visible for the authoritative duration.
  status.emote.animationFrame = Math.floor((now-status.started)/150) % 4;
  status.emote.ticks = EMOTE_ANIMATION_TICKS;
  return status.emote;
}
